import { ReactNode, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { ControllableApplicationRouter } from '@cld/application-router';
import { NavigationSkeleton } from '@cld/navigation-menu';
import {
  AddNewUserPrimaryItem,
  DefaultPrimaryMenuMobile,
  DefaultPrimaryMenu,
  DefaultSideMenuMobile,
  DefaultSideMenu,
  DefaultWorkspaceMenu,
  DefaultWorkspaceMenuMobile,
  ApplicationWrapperConnected,
  AppId,
  BottomPrimaryMenuItemsMobile,
  BottomPrimaryMenuItems,
  useUserData,
  CUSTOMER_ID_PREFIX_FOR_URL,
} from '@cld-remote/application_wrapper/index';

import { SideMenuContent } from './components';
import { createConsoleUrl } from './utils/console';
import { AppDataLoader, AppDataSuspense, useAppData } from './context/app-context';
import { Loader } from './components/loader';
import { UnicornApi } from '@cld/unicorn-api';
import { TreatmentValue } from './types/treatments';

const PrimaryMenuBottomItems = ({ isMobile = false }) => {
  const { features } = useAppData();
  const { isAllowedToCreateUsers } = features || {};
  return isAllowedToCreateUsers && !isMobile ? <AddNewUserPrimaryItem /> : null;
};

export const AppWrapper = ({
  isMobile = false,
  children,
  unicornApi,
  optimizationReportsLinkTreatment,
  mediaExplorerDeprecationTreatment,
}: {
  children?: ReactNode;
  isMobile?: boolean;
  unicornApi: UnicornApi;
  optimizationReportsLinkTreatment: TreatmentValue;
  mediaExplorerDeprecationTreatment: TreatmentValue;
}) => {
  const { customerId } = useParams();
  const userData = useUserData();
  const routerUserData = useMemo(() => ({ userData }), [userData]);

  const PrimaryMenu = isMobile ? DefaultPrimaryMenuMobile : DefaultPrimaryMenu;
  const SideMenu = isMobile ? DefaultSideMenuMobile : DefaultSideMenu;
  const WorkspaceMenu = isMobile ? DefaultWorkspaceMenuMobile : DefaultWorkspaceMenu;
  const isLocationMarketplace = /pm\/c-[^/]*\/marketplace/.test(window.location.pathname);
  // TODO: Get rid of `ApplicationRouter` in favor of top level routes defined via `createBrowserRouter`
  return (
    <ControllableApplicationRouter
      currentCustomer={{ externalIdForUrl: `/c-${unicornApi.data.productEnvironment.externalId}` }}
      userData={routerUserData}
      customerIdPrefixForUrl={CUSTOMER_ID_PREFIX_FOR_URL}
      notFoundComponent={<p>not found</p>}
    >
      <AppDataLoader unicornApi={unicornApi}>
        <ApplicationWrapperConnected
          unicornApi={unicornApi}
          isMobile={isMobile}
          onAppSelect={(appId) => {
            if (appId === AppId.ProductExplorer) {
              window.location.assign(createConsoleUrl('home/product-explorer', customerId));
            }
          }}
          selectedAppId={AppId.MediaAPI}
          primaryMenu={
            <PrimaryMenu
              bottomItems={
                <>
                  <AppDataSuspense>
                    <PrimaryMenuBottomItems isMobile={isMobile} />
                  </AppDataSuspense>
                  {isMobile ? <BottomPrimaryMenuItemsMobile /> : <BottomPrimaryMenuItems />}
                </>
              }
            />
          }
          sideBarMenu={
            !isLocationMarketplace && (
              <SideMenu header={<WorkspaceMenu />}>
                {
                  <AppDataSuspense fallback={<NavigationSkeleton />}>
                    <SideMenuContent
                      themeName={unicornApi.data.theme.name}
                      optimizationReportsLinkTreatment={optimizationReportsLinkTreatment}
                      mediaExplorerDeprecationTreatment={mediaExplorerDeprecationTreatment}
                    />
                  </AppDataSuspense>
                }
              </SideMenu>
            )
          }
        >
          <AppDataSuspense fallback={<Loader />}>{children}</AppDataSuspense>
        </ApplicationWrapperConnected>
      </AppDataLoader>
    </ControllableApplicationRouter>
  );
};
